import {useTranslation} from 'react-i18next'
import {type Readiness} from '@/lib/sgsst-readiness'
import {cn} from '@/lib/utils'

export type AuditFilter = 'sin-soporte' | 'vigencia'

function Chip({
  count,
  label,
  pressed,
  onToggle,
  testId,
}: {
  count: number
  label: string
  pressed: boolean
  onToggle: () => void
  testId: string
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={pressed}
      disabled={count === 0}
      onClick={onToggle}
      className={cn(
        'rounded-full border px-3 py-1 font-mono text-[11px] tracking-wide',
        pressed
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input text-muted-foreground hover:text-foreground',
        count === 0 && 'cursor-not-allowed opacity-50 hover:text-muted-foreground'
      )}>
      {label}
    </button>
  )
}

/** On a closed year nothing is confirmable, so the pending chip is omitted. */
export function ReadinessStrip({
  audit,
  isClosed,
  filter,
  onToggle,
}: {
  audit: Readiness
  isClosed: boolean
  filter: string | null
  onToggle: (chip: AuditFilter) => void
}) {
  const {t} = useTranslation()
  return (
    <div data-testid="readiness-strip" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('sgsst.readiness.eyebrow')}
        </p>
        {audit.total === 0 ? (
          <p className="text-sm text-muted-foreground">{t('sgsst.readiness.empty')}</p>
        ) : (
          <p className="text-sm text-foreground">
            <span className="font-mono text-xs font-medium">
              {t('sgsst.readiness.count', {covered: audit.covered, total: audit.total})}
            </span>{' '}
            {t('sgsst.readiness.suffix')}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Chip
          testId="readiness-chip-no-evidence"
          count={audit.sinSoporte.length}
          label={t('sgsst.readiness.chipNoEvidence', {count: audit.sinSoporte.length})}
          pressed={filter === 'sin-soporte'}
          onToggle={() => onToggle('sin-soporte')}
        />
        {!isClosed && (
          <Chip
            testId="readiness-chip-pending"
            count={audit.pendingVigencia.length}
            label={t('sgsst.readiness.chipPending', {count: audit.pendingVigencia.length})}
            pressed={filter === 'vigencia'}
            onToggle={() => onToggle('vigencia')}
          />
        )}
      </div>
    </div>
  )
}
